<template>
  <article id="insta-wrapper" class="contents-wrapper">
    <div class="page-header">
      <div class="title">언팔 찾기</div>
      <div class="subtitle">내가 팔로우하지만 나를 팔로우하지 않는 계정을 찾아드려요</div>
    </div>

    <!-- 개인정보 안심 배너 -->
    <div class="privacy-banner">
      <span class="pb-ic">🔒</span>
      <span class="pb-text">
        업로드한 파일은 <b>서버로 전송되지 않고</b> 브라우저 안에서만 분석됩니다. 저장·수집도 하지 않아요.
      </span>
    </div>

    <!-- Upload Section -->
    <div class="upload-section">
      <div class="upload-header">맞팔 아닌 팔로잉 찾기</div>

      <!-- 데이터 준비 안내 (결과 전에만) -->
      <div class="prep-steps" v-if="!hasResult">
        <div class="prep-step">
          <span class="ps-num">1</span>
          <span class="ps-text">
            <a href="https://www.instagram.com/download/request/" target="_blank" rel="noopener noreferrer">인스타그램 데이터 다운로드 →</a>
            에서 <b>JSON 형식</b>으로 정보 요청
          </span>
        </div>
        <div class="prep-step">
          <span class="ps-num">2</span>
          <span class="ps-text">메일로 받은 <b>ZIP 파일</b>을 다운로드 (요청 후 몇 분~몇 시간 소요)</span>
        </div>
        <div class="prep-step">
          <span class="ps-num">3</span>
          <span class="ps-text">ZIP을 그대로 아래에 올리면 끝 — 압축 풀 필요 없어요</span>
        </div>
        <router-link to="/insta/home" class="prep-more">자세한 그림 가이드 보기 →</router-link>
      </div>

      <!-- ZIP Upload (Primary) -->
      <div
        class="drop-zone"
        :class="{ 'drag-over': isDragOver }"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
        @drop.prevent="handleDrop"
      >
        <div class="drop-zone-icon">📁</div>
        <div class="drop-zone-text">
          Instagram 데이터 ZIP 파일을 여기에 드래그하세요
        </div>
        <div class="drop-zone-sub">또는</div>
        <label class="file-select-btn" for="zip-upload">파일 선택</label>
        <input
          type="file"
          id="zip-upload"
          accept=".zip"
          class="file-input-hidden"
          @change="handleZipUpload"
        />
        <div class="drop-zone-hint">ZIP 또는 JSON 파일 지원</div>
      </div>

      <!-- Loading -->
      <div class="loading-area" v-if="isLoading">
        <div class="spinner"></div>
        <span>파일을 분석하고 있습니다...</span>
      </div>

      <!-- Error -->
      <div class="error-message" v-if="errorMessage">
        {{ errorMessage }}
      </div>

      <!-- JSON Fallback (Collapsible) -->
      <div class="json-fallback">
        <div
          class="json-fallback-toggle"
          @click="showJsonUpload = !showJsonUpload"
        >
          {{ showJsonUpload ? "▲" : "▼" }} JSON 파일로 직접 업로드
        </div>
        <div class="json-fallback-content" v-if="showJsonUpload">
          <div class="json-file-row">
            <label for="file-upload1" class="json-file-btn"
              >followers 파일</label
            >
            <input
              type="file"
              id="file-upload1"
              accept=".json"
              class="file-input-hidden"
              @change="handleFileUpload1"
            />
            <span class="json-file-name">{{ fileName1 }}</span>
          </div>
          <div class="json-file-row">
            <label for="file-upload2" class="json-file-btn"
              >following 파일</label
            >
            <input
              type="file"
              id="file-upload2"
              accept=".json"
              class="file-input-hidden"
              @change="handleFileUpload2"
            />
            <span class="json-file-name">{{ fileName2 }}</span>
          </div>
          <div class="json-analyze-btn" @click="getResult()">결과보기</div>
        </div>
      </div>
    </div>

    <!-- Result Section -->
    <div class="result-section" v-if="hasResult">
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div
          class="summary-card"
          :class="{ active: activeTab === 'unfollowers' }"
          @click="activeTab = 'unfollowers'"
        >
          <div class="summary-count">{{ unfollowers.length }}</div>
          <div class="summary-label">맞팔 안한 사람</div>
        </div>
        <div
          class="summary-card"
          :class="{ active: activeTab === 'mutuals' }"
          @click="activeTab = 'mutuals'"
        >
          <div class="summary-count">{{ mutuals.length }}</div>
          <div class="summary-label">맞팔</div>
        </div>
        <div
          class="summary-card"
          :class="{ active: activeTab === 'onlyFollowers' }"
          @click="activeTab = 'onlyFollowers'"
        >
          <div class="summary-count">{{ onlyFollowers.length }}</div>
          <div class="summary-label">팔로워만</div>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <input
          type="text"
          class="search-input"
          v-model="searchQuery"
          placeholder="사용자 검색..."
        />
        <select class="sort-select" v-model="sortOption">
          <option value="name-asc">이름 오름차순</option>
          <option value="name-desc">이름 내림차순</option>
          <option value="date-asc">날짜 오래된 순</option>
          <option value="date-desc">날짜 최신 순</option>
        </select>
        <button class="tool-btn" @click="handleExportCSV" title="CSV 내보내기">
          CSV
        </button>
        <button
          class="tool-btn"
          @click="handleCopyUsernames"
          title="사용자명 복사"
        >
          {{ copyLabel }}
        </button>
      </div>

      <!-- User List -->
      <div class="user-list">
        <div class="user-list-header">
          {{ tabTitle }} ({{ filteredUsers.length }}명)
        </div>
        <div
          class="user-card"
          v-for="user in visibleUsers"
          :key="user.value"
        >
          <div class="user-card-main">
            <span class="user-name" @click="openLink(user.href)">{{
              user.value
            }}</span>
            <button
              class="copy-single-btn"
              @click.stop="copySingle(user.value)"
              title="사용자명 복사"
            >
              복사
            </button>
          </div>
          <div class="user-card-sub">
            <span class="follow-date">{{
              getDateFormatByTimestamp(user.timestamp, "yyyy년 MM월 dd일")
            }}</span>
            <a
              class="profile-link"
              :href="user.href"
              target="_blank"
              @click.stop
              >프로필</a
            >
          </div>
        </div>
        <div class="load-more-row" v-if="filteredUsers.length > visibleCount">
          <button class="load-more-btn" @click="visibleCount += PAGE_SIZE">
            더 보기 ({{ visibleUsers.length }}/{{ filteredUsers.length }})
          </button>
        </div>
        <div class="empty-state" v-if="filteredUsers.length === 0">
          검색 결과가 없습니다.
        </div>
      </div>

      <div class="reset-row">
        <button class="reset-btn" @click="resetAll">다른 파일로 다시 분석</button>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { processZipFile } from "@/insta/utils/zipProcessor";
import { exportCSV, copyUsernames } from "@/insta/utils/exportUtil";
import type { InstaUser, SortOption } from "@/insta/types/instaTypes";

export default defineComponent({
  name: "InstaUnfolowerPage",
  mixins: [UtilMixin],

  data() {
    return {
      // Upload state
      isDragOver: false,
      isLoading: false,
      errorMessage: "",
      showJsonUpload: false,

      // JSON fallback
      file1: null as File | null,
      file2: null as File | null,
      fileName1: "-",
      fileName2: "-",

      // Raw data
      followers: [] as InstaUser[],
      following: [] as InstaUser[],

      // Result
      unfollowers: [] as InstaUser[],
      mutuals: [] as InstaUser[],
      onlyFollowers: [] as InstaUser[],
      hasResult: false,

      // UI state
      activeTab: "unfollowers" as "unfollowers" | "mutuals" | "onlyFollowers",
      searchQuery: "",
      sortOption: "name-asc" as SortOption,
      copyLabel: "복사",

      // 대용량 목록 페이징
      PAGE_SIZE: 50,
      visibleCount: 50,
    };
  },

  watch: {
    activeTab() {
      this.visibleCount = this.PAGE_SIZE;
    },
    searchQuery() {
      this.visibleCount = this.PAGE_SIZE;
    },
    sortOption() {
      this.visibleCount = this.PAGE_SIZE;
    },
  },

  computed: {
    tabTitle(): string {
      const titles = {
        unfollowers: "맞팔 안한 사람",
        mutuals: "맞팔",
        onlyFollowers: "팔로워만",
      };
      return titles[this.activeTab];
    },

    currentList(): InstaUser[] {
      const lists = {
        unfollowers: this.unfollowers,
        mutuals: this.mutuals,
        onlyFollowers: this.onlyFollowers,
      };
      return lists[this.activeTab];
    },

    filteredUsers(): InstaUser[] {
      let list = this.currentList;

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter((u) => u.value.toLowerCase().includes(q));
      }

      const sorted = [...list];
      switch (this.sortOption) {
        case "name-asc":
          sorted.sort((a, b) => a.value.localeCompare(b.value));
          break;
        case "name-desc":
          sorted.sort((a, b) => b.value.localeCompare(a.value));
          break;
        case "date-asc":
          sorted.sort((a, b) => a.timestamp - b.timestamp);
          break;
        case "date-desc":
          sorted.sort((a, b) => b.timestamp - a.timestamp);
          break;
      }
      return sorted;
    },

    visibleUsers(): InstaUser[] {
      return this.filteredUsers.slice(0, this.visibleCount);
    },
  },

  methods: {
    // --- Drag & Drop ---
    handleDrop(event: DragEvent) {
      this.isDragOver = false;
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;
      this.processFile(files[0]);
    },

    handleZipUpload(event: Event) {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (!files || files.length === 0) return;
      this.processFile(files[0]);
    },

    async processFile(file: File) {
      this.errorMessage = "";

      if (file.name.endsWith(".zip")) {
        this.isLoading = true;
        try {
          const result = await processZipFile(file);
          this.followers = result.followers;
          this.following = result.following;
          this.calculateResult();
        } catch (e: any) {
          this.errorMessage = e.message || "ZIP 파일 처리 중 오류가 발생했습니다.";
        } finally {
          this.isLoading = false;
        }
      } else if (file.name.endsWith(".json")) {
        this.errorMessage =
          "JSON 파일은 아래 'JSON 파일로 직접 업로드' 섹션을 이용해주세요.";
        this.showJsonUpload = true;
      } else {
        this.errorMessage = "ZIP 또는 JSON 파일만 업로드할 수 있습니다.";
      }
    },

    // --- JSON Fallback ---
    handleFileUpload1(event: Event) {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (!files || files.length === 0) return;

      this.file1 = files[0];
      if (!this.file1.name.endsWith(".json")) {
        this.errorMessage = "JSON 파일만 업로드 가능합니다.";
        return;
      }

      this.errorMessage = "";
      this.readJsonFile(this.file1, (data) => {
        try {
          this.followers = data.map(
            (item: { string_list_data: any[] }) => {
              const user = item.string_list_data[0];
              return {
                value: user.value,
                href: user.href,
                timestamp: user.timestamp,
              };
            }
          );
        } catch {
          this.errorMessage = "followers 파일 형식이 올바르지 않습니다.";
        }
      });
      this.fileName1 = files[0].name;
    },

    handleFileUpload2(event: Event) {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (!files || files.length === 0) return;

      this.file2 = files[0];
      if (!this.file2.name.endsWith(".json")) {
        this.errorMessage = "JSON 파일만 업로드 가능합니다.";
        return;
      }

      this.errorMessage = "";
      this.readJsonFile(this.file2, (data) => {
        try {
          this.following = data.relationships_following.map(
            (item: { string_list_data: any[] }) => {
              const user = item.string_list_data[0];
              return {
                value: user.value,
                href: user.href,
                timestamp: user.timestamp,
              };
            }
          );
        } catch {
          this.errorMessage = "following 파일 형식이 올바르지 않습니다.";
        }
      });
      this.fileName2 = files[0].name;
    },

    readJsonFile(file: File, callback: (data: any) => void) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const result = JSON.parse(e.target!.result as string);
          callback(result);
        } catch {
          this.errorMessage = "잘못된 JSON 파일입니다. 올바른 파일을 업로드해주세요.";
        }
      };
      reader.onerror = () => {
        this.errorMessage = "파일을 읽는 중 오류가 발생했습니다.";
      };
      reader.readAsText(file);
    },

    // --- Result Calculation ---
    getResult() {
      if (this.followers.length === 0 || this.following.length === 0) {
        this.errorMessage = "followers와 following 파일을 모두 업로드해주세요.";
        return;
      }
      this.errorMessage = "";
      this.calculateResult();
    },

    calculateResult() {
      const followerSet = new Set(this.followers.map((p) => p.value));
      const followingSet = new Set(this.following.map((p) => p.value));

      this.unfollowers = this.following.filter(
        (p) => !followerSet.has(p.value)
      );
      this.mutuals = this.following.filter((p) => followerSet.has(p.value));
      this.onlyFollowers = this.followers.filter(
        (p) => !followingSet.has(p.value)
      );

      this.hasResult = true;
      this.activeTab = "unfollowers";
    },

    resetAll() {
      this.followers = [];
      this.following = [];
      this.unfollowers = [];
      this.mutuals = [];
      this.onlyFollowers = [];
      this.hasResult = false;
      this.searchQuery = "";
      this.visibleCount = this.PAGE_SIZE;
      this.file1 = null;
      this.file2 = null;
      this.fileName1 = "-";
      this.fileName2 = "-";
      this.errorMessage = "";
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // --- Actions ---
    openLink(link: string) {
      if (link) {
        window.open(link, "_blank");
      }
    },

    handleExportCSV() {
      const filenames = {
        unfollowers: "맞팔안한사람",
        mutuals: "맞팔",
        onlyFollowers: "팔로워만",
      };
      exportCSV(this.filteredUsers, filenames[this.activeTab]);
    },

    async handleCopyUsernames() {
      const success = await copyUsernames(this.filteredUsers);
      this.copyLabel = success ? "복사됨!" : "실패";
      setTimeout(() => {
        this.copyLabel = "복사";
      }, 2000);
    },

    async copySingle(username: string) {
      try {
        await navigator.clipboard.writeText(username);
      } catch {
        // silent fail
      }
    },
  },
});
</script>
